import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ymd(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fmtSlotLabel(iso) {
  const [, t] = iso.split("T");
  const [h, m] = t.split(":");
  let hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  if (hour === 0) hour = 12;
  else if (hour > 12) hour -= 12;
  return `${hour}:${m} ${ampm}`;
}

const SlotTimes = ({ date, loading, slots, slot, onSelect }) => {
  if (!date) {
    return <p className="text-sm text-[var(--gg-text-muted)] italic">Select a date first to see available times.</p>;
  }
  if (loading) {
    return <p className="text-sm text-[var(--gg-text-muted)]">Loading times…</p>;
  }
  if (slots.length === 0) {
    return <p className="text-sm text-[var(--gg-text-muted)]">No slots available on this day. Please pick another date.</p>;
  }
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="slot-list">
      {slots.map((s) => {
        const active = slot === s;
        return (
          <button
            type="button"
            key={s}
            onClick={() => onSelect(s)}
            data-testid={`slot-${s}`}
            className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${
              active
                ? "bg-[var(--gg-primary)] text-white border-[var(--gg-primary)]"
                : "border-[var(--gg-border)] text-[var(--gg-deep)] hover:border-[var(--gg-primary)] hover:bg-[var(--gg-primary)]/5"
            }`}
          >
            {fmtSlotLabel(s)}
          </button>
        );
      })}
    </div>
  );
};

export const SlotPicker = ({ slot, onSlotChange }) => {
  const [date, setDate] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 60);
    return d;
  }, []);

  useEffect(() => {
    if (!date) {
      setSlots([]);
      return undefined;
    }
    let cancelled = false;
    const fetchSlots = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/consultations/available-slots`, {
          params: { date: ymd(date) },
        });
        if (!cancelled) setSlots(data?.slots || []);
      } catch {
        if (!cancelled) setSlots([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSlots();
    return () => { cancelled = true; };
  }, [date]);

  const isDisabled = (d) => {
    if (d < minDate) return true;
    if (d > maxDate) return true;
    const day = d.getDay();
    if (day === 0 || day === 6) return true;
    return false;
  };

  const onDateSelect = (d) => {
    setDate(d);
    onSlotChange("");
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <Label className="text-xs uppercase tracking-[0.18em] text-[var(--gg-text-muted)] font-semibold flex items-center gap-2">
          <CalendarDays size={14} /> Pick a date <span className="text-[var(--gg-primary)]">*</span>
        </Label>
        <div className="mt-3 border border-[var(--gg-border)] rounded-xl p-3 bg-white">
          <Calendar
            mode="single"
            selected={date}
            onSelect={onDateSelect}
            disabled={isDisabled}
            fromDate={minDate}
            toDate={maxDate}
            data-testid="date-picker"
          />
        </div>
        <p className="text-xs text-[var(--gg-text-muted)] mt-2">
          Mon–Fri only · minimum 24-hour notice
        </p>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-[0.18em] text-[var(--gg-text-muted)] font-semibold flex items-center gap-2">
          <Clock size={14} /> Pick a time (Jamaica) <span className="text-[var(--gg-primary)]">*</span>
        </Label>
        <div className="mt-3">
          <SlotTimes date={date} loading={loading} slots={slots} slot={slot} onSelect={onSlotChange} />
        </div>
      </div>
    </div>
  );
};
